import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Container
} from '@mui/material';
import { 
  ErrorOutline 
} from '@mui/icons-material';
import { auth } from '../../services/firebase';
import { 
  applyActionCode, 
  checkActionCode,
  reload as reloadUser
} from 'firebase/auth';

function EmailActionHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [actionType, setActionType] = useState('');

  useEffect(() => {
    const handleEmailAction = async () => {
      // Get the action to complete
      const mode = searchParams.get('mode');
      const actionCode = searchParams.get('oobCode');
      const continueUrl = searchParams.get('continueUrl');

      console.log('Email action handler triggered:', { mode, actionCode, continueUrl });

      if (!mode || !actionCode) {
        setStatus('error');
        setMessage('Invalid verification link. Please request a new verification email.');
        return;
      }

      try {
        // Verify the action code is valid
        const info = await checkActionCode(auth, actionCode);
        console.log('Action code info:', info);
        setActionType(mode);

        switch (mode) {
          case 'verifyEmail':
            try {
              // Apply the email verification code
              await applyActionCode(auth, actionCode);
              console.log('Email verification code applied successfully');

              // If user is currently logged in, reload to update their session
              if (auth.currentUser) {
                console.log('User is logged in, reloading user data...');
                await reloadUser(auth.currentUser);
                console.log('User reloaded, emailVerified:', auth.currentUser.emailVerified);
                
                // Force token refresh to update custom claims
                await auth.currentUser.getIdToken(true);
                console.log('Token refreshed');
                
                // Redirect to homepage with success flag
                navigate('/?verified=true');
                return;
              } else {
                console.log('User is not logged in, verification applied but user needs to log in to see changes');
                setStatus('success');
                setMessage('Your email has been verified successfully! Please log in to continue.');
              }
              
              // Clear the email verification dismissed flag
              sessionStorage.removeItem('emailVerificationDismissed');
              localStorage.removeItem('emailVerificationDismissed');
            } catch (verifyError) {
              console.error('Error verifying email:', verifyError);
              throw verifyError;
            }
            break;
          case 'resetPassword':
            // Navigate to password reset screen with the code
            navigate(`/reset-password?oobCode=${actionCode}`);
            break;
          case 'recoverEmail':
            try {
              await applyActionCode(auth, actionCode);
              setStatus('success');
              setMessage('Your email has been recovered successfully.');
            } catch (recoverError) {
              console.error('Error recovering email:', recoverError);
              throw recoverError;
            }
            break;
          default:
            setStatus('error');
            setMessage('Unknown action type.');
        }
      } catch (error) {
        console.error('Error handling email action:', error);
        setStatus('error');
        
        switch (error.code) {
          case 'auth/expired-action-code':
            setMessage('This verification link has expired. Please request a new one.');
            break;
          case 'auth/invalid-action-code':
            setMessage('This verification link is invalid. Please request a new one.');
            break;
          case 'auth/user-disabled':
            setMessage('This account has been disabled.');
            break;
          case 'auth/user-not-found':
            setMessage('User account not found.');
            break;
          default:
            setMessage(`Error: ${error.message}`);
        }
      }
    };

    handleEmailAction();
  }, [searchParams, navigate]);



  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {status === 'loading' && (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h5" gutterBottom>
                  Processing...
                </Typography>
                <Typography color="text.secondary">
                  Please wait while we verify your email.
                </Typography>
              </>
            )}

            {status === 'error' && (
              <>
                <ErrorOutline
                  sx={{
                    fontSize: 80,
                    color: 'error.main',
                    mb: 2
                  }}
                />
                <Typography variant="h5" gutterBottom color="error.main">
                  Verification Failed
                </Typography>
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  {message}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/')}
                    fullWidth
                  >
                    Go to Home
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/settings')}
                    fullWidth
                  >
                    Go to Settings
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default EmailActionHandler;
